import type { PricingCampaignModel } from '@/shared/generated'

import React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { propEq } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useWithAffectedOGsHandler } from '@/entities/optimization-group'
import {
	useGlobalPcSetQuery,
	useUpdatePricingCampaignMutation,
} from '@/entities/pricing-campaign'
import { Access } from '@/entities/session'

import { useModals } from '@/shared/lib/modal-manager'
import { queryClient } from '@/shared/lib/query-client'
import { processingMessage } from '@/shared/tools/message'

export function RenamePricingCampaign({
	children,
	pricingCampaign,
}: {
	pricingCampaign: PricingCampaignModel
	children: React.ReactNode
}) {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const { data: isMainGlobal } = useGlobalPcSetQuery<boolean>(
		pricingCampaign.global_pc_set_id!,
		{
			enabled: Boolean(pricingCampaign.global_pc_set_id),
			select: propEq('is_main', true),
		}
	)
	const baseUpdatePricingCampaignMutation = useUpdatePricingCampaignMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
		},
	})
	const updateWith424Handler = useWithAffectedOGsHandler(
		baseUpdatePricingCampaignMutation
	)
	const updatePricingCampaignMutation = isMainGlobal
		? updateWith424Handler
		: baseUpdatePricingCampaignMutation
	const updatePricingCampaign = async (name: string) => {
		toast.promise(
			updatePricingCampaignMutation.mutateAsync({
				id: pricingCampaign.id,
				name,
			}),
			{
				loading: processingMessage(t),
				success: t('toast.entity.updated', {
					entity: t('entity.pricing_campaign'),
				}),
				error: t('pricing_campaign.rename.processing.error', {
					defaultValue: 'Error while renaming pricing campaign',
				}),
			}
		)
	}
	const handleRename = () =>
		showModal('GENERIC_RENAME_MODAL', {
			title: t('app.edit_pc_name', {
				defaultValue: 'Edit pricing campaign name',
			}),
			value: pricingCampaign.name,
			onChange: updatePricingCampaign,
		})

	return (
		<Access permissions={['EDIT_GLOBAL_PRICING_CAMPAIGNS_SETTINGS']}>
			<Slot onClick={handleRename}>{children}</Slot>
		</Access>
	)
}
